import {
  CloseOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Input, Link, Tooltip } from '@mm/components';
import { useSendGridCRUDActions } from '@mm/state/actions/SendGridCRUDActions';
import { EmailAccountSendGridCredentialType } from '@mm/state/interfaces/emailAccount';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectActiveEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Checkbox, Alert } from 'antd';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';
import styles from './ConnectSendGridModal.module.css';

export const ConnectSendGridModal = () => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const sendGridAccounts = useSelector(
    (state: StoreState) => state.sendGrid.accounts,
  );

  const userFromSession = useSelector(selectSessionSelector);
  const emailAccounts = useSelector(selectActiveEmailAccounts);
  const existingAccCredential = emailAccounts?.find(
    acc =>
      acc.kind === 'sendgrid' &&
      acc.credential?.accessTokenMasked &&
      acc.credential?.userId === userFromSession?.id,
  )?.credential as EmailAccountSendGridCredentialType;

  const existingApiKey = existingAccCredential?.accessTokenMasked;

  const [apiKey, setApiKey] = useState(existingApiKey || '');
  const [error, setError] = useState('');
  const [emails, setEmails] = useState<string[] | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [activeStep, setActiveStep] = useState<1 | 2>(1);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const availbelEmails = emails?.filter(
    email => !emailAccounts.find(acc => acc.emailAddress === email),
  );
  const isAllSelected = isEqual(availbelEmails?.sort(), selectedEmails.sort());

  const { closeModal } = useModalActions();
  const { getAccounts, createAccounts } = useSendGridCRUDActions();

  const sendGridApiKeysLink = 'https://app.sendgrid.com/settings/api_keys';
  const helpLink =
    'https://customers.ai/help/l/en/article/f8rlpppphp-send-grid-integration';

  const key =
    apiKey === existingApiKey ? existingAccCredential.id : apiKey.trim();

  const onNextClick = async () => {
    if (
      (apiKey.startsWith('SG.') && apiKey.length > 60) ||
      apiKey === existingApiKey
    ) {
      try {
        setActiveStep(2);
        setBusy(true);

        const res = await getAccounts(key);
        if (res) {
          setEmails(Object.keys(res.emails));
        }
      } catch (e) {
      } finally {
        setBusy(false);
      }
    } else {
      setError('API Key is invalid');
    }
  };

  const onConnectAccountsClick = async () => {
    const selectedAccounts = selectedEmails.map(email => ({
      [email]: sendGridAccounts?.[email],
    }));
    const mergedAccounts = Object.assign({}, ...selectedAccounts);

    await createAccounts(key, facebookPageId, mergedAccounts);
    closeModal();
  };

  const isCheckedEmail = email => !!selectedEmails.includes(email);

  const onSelectAll = () => {
    if (isAllSelected) {
      setSelectedEmails([]);
    } else {
      setSelectedEmails(availbelEmails || []);
    }
  };

  const onChangeSelectedEmails = email => {
    if (isCheckedEmail(email)) {
      setSelectedEmails(prevState =>
        prevState.filter(selectedEmail => selectedEmail !== email),
      );
    } else {
      setSelectedEmails(prevState => [...prevState, email]);
    }
  };

  return (
    <div className={styles.container}>
      <CloseOutlined
        className={styles.closeIcon}
        onClick={() => closeModal()}
      />

      <div className={styles.block}>
        <div className={styles.step}>{activeStep} out of 2</div>
        <div className={styles.heading}>Connect with SendGrid</div>
      </div>
      {activeStep === 1 && (
        <>
          <div className={classNames(styles.block, styles.links)}>
            <Link href={sendGridApiKeysLink} isBlank>
              1. Generate Sendgrid API Key
            </Link>
            <Link href={helpLink} isBlank>
              2. Read how to connect SendGrid
            </Link>
          </div>

          <div className={styles.block}>
            <span style={{ fontWeight: 500 }}>SendGrid API Key</span>{' '}
            <Input
              type="text"
              css={{ container: styles.input }}
              placeholder="Enter API key"
              value={apiKey}
              onChange={e => {
                setApiKey(e.target.value);
                setError('');
              }}
            />
            {error && <div className={styles.error}>{error}</div>}
          </div>

          <PrimaryButton
            className={styles.button}
            onClick={onNextClick}
            disabled={!!error || !apiKey}
          >
            Next
          </PrimaryButton>
        </>
      )}

      {activeStep === 2 && (
        <>
          {busy ? (
            <Alert
              className={classNames(styles.alert, styles.infoAlert)}
              message="Getting your emails from SendGrid."
              description="Please, don’t close this pop-up."
              type="info"
              showIcon
              icon={<LoadingOutlined className={styles.loadingIcon} />}
            />
          ) : !emails?.length && !busy ? (
            <>
              <Alert
                className={classNames(styles.alert, styles.warningAlert)}
                message="There are no emails connected to SendGrid"
                description={
                  <div>
                    Connect your emails to SendGrid {''}{' '}
                    <Link
                      href={sendGridApiKeysLink}
                      isBlank
                      className={styles.blackLink}
                    >
                      here
                    </Link>{' '}
                  </div>
                }
                showIcon
                icon={
                  <ExclamationCircleOutlined
                    className={styles.exclamationIcon}
                  />
                }
              />
              <PrimaryButton
                className={styles.button}
                onClick={() => {
                  window.open(sendGridApiKeysLink);
                  closeModal();
                }}
              >
                Go to SendGrid
              </PrimaryButton>
            </>
          ) : (
            <>
              <div className={styles.block}>
                <div style={{ fontWeight: 500 }}>
                  Connect your email accounts:
                </div>
                <div className={styles.checkboxesWrapper}>
                  {(emails?.length || 0) >= 3 && (
                    <Checkbox
                      className={styles.checkbox}
                      indeterminate={
                        Boolean(selectedEmails.length) && !isAllSelected
                      }
                      onChange={onSelectAll}
                      checked={isAllSelected}
                    >
                      <span style={{ fontWeight: 500 }}>
                        All ({emails?.length})
                      </span>
                    </Checkbox>
                  )}
                  {emails?.map(email => {
                    const sendGridAccount = sendGridAccounts?.[email];
                    const connectedAccount = emailAccounts.some(
                      acc => acc.emailAddress === email,
                    );
                    return (
                      <Tooltip
                        key={email}
                        placement="bottomRight"
                        title="Email is already connected"
                        overlayClassName={classNames({
                          [styles.tooltipHidden]: !connectedAccount,
                        })}
                        getPopupContainer={element =>
                          element.parentElement
                            ? element.parentElement
                            : document.body
                        }
                      >
                        <Checkbox
                          key={email}
                          className={styles.checkbox}
                          checked={isCheckedEmail(email)}
                          onChange={() => onChangeSelectedEmails(email)}
                          disabled={connectedAccount}
                        >
                          <div className={styles.checkboxLabel}>
                            {email}
                            {!sendGridAccount.verified && !connectedAccount && (
                              <Tooltip
                                title="Email is not verified"
                                getPopupContainer={element =>
                                  element.parentElement
                                    ? element.parentElement
                                    : document.body
                                }
                              >
                                <ExclamationCircleOutlined
                                  className={styles.nonVerified}
                                />
                              </Tooltip>
                            )}
                          </div>
                        </Checkbox>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
              <PrimaryButton
                className={styles.button}
                onClick={onConnectAccountsClick}
                disabled={!selectedEmails.length}
              >
                Connect SendGrid
              </PrimaryButton>
            </>
          )}
        </>
      )}
    </div>
  );
};
